/**
 * EL INFORME DEL QUINCENAL (artefacto «El reporte quincenal, pantallas», 26-10).
 *
 *     INFORME QUINCENAL
 *     RESUMEN DE TUS ÚLTIMOS 11 REGISTROS
 *     Del sábado 26 de septiembre al martes 6 de octubre
 *                   −0,7 kg
 *          desde el último punto de control
 *     TU PESO              Peso de hoy · Último registro · Peso semanal anterior ·
 *                          Último punto de control
 *     LO QUE HAS HECHO     Check-in 9/11 ... con su barra
 *     CÓMO TE HAS SENTIDO · sobre 9 check-in      (estrellas; la saciedad, con la respuesta
 *                                                  más repetida y en cuántos días)
 *     [Continuar]
 *
 * Todo sale ya contado de `GET /reports/quincenal/resumen` (`core/quincenal`): aquí no se
 * calcula nada. Sin nada registrado, en vez de lo hecho y las sensaciones, el texto del
 * artefacto y el peso de hoy con el último punto de control.
 *
 * SIN EL PESO DE HOY NO SE ABRE (artefacto: «el peso se registra antes de poder abrirlo»).
 * El reporte guarda su peso con la fecha del día en que se manda, y el informe compara contra
 * el de hoy. Si hoy (en su reloj, `hoyLocal`) no se ha pesado, antes del informe sale la
 * pantalla «Tu peso de hoy» (`PuertaDelPeso`), con el peso de referencia semanal si toca, y
 * «Abrir el reporte quincenal». Vale igual para quien llega desde Seguimiento y desde la
 * campana: la puerta está aquí y no en la tarjeta.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { CabeceraDelQuincenal, BarraDeTramos, FilasDePeso, FilasDeSensaciones,
    TarjetaDelResumen as Seccion, ValorDelResumen as Valor } from './piezas';
import CampoDePeso from '../CampoDePeso';
import { hoyLocal } from '../../lib/horaEspana';

const ORANGE = '#FF671F';

/** «Miércoles, 7 de octubre». */
const hoyConNombre = () => {
    const t = new Date(`${hoyLocal()}T12:00:00`).toLocaleDateString('es-ES',
        { weekday: 'long', day: 'numeric', month: 'long' });
    return t.charAt(0).toUpperCase() + t.slice(1);
};

/** TU PESO DE HOY, antes de abrir el informe. */
const PuertaDelPeso = ({ api, ultimo, onAbrir }) => {
    const [pesado, setPesado] = useState(null);
    const [semana, setSemana] = useState(null);
    // Mientras se le ofrece el peso de referencia, primero eso: Confirmar o Modificar.
    const listo = pesado && !semana?.ofrecer;
    return (
        <div className="space-y-4" data-testid="quincenal-puerta-peso">
            <div>
                <h2 className="text-2xl font-bold text-foreground uppercase"
                    style={{ fontFamily: 'Barlow Condensed', letterSpacing: '0.02em' }}>
                    Tu peso de hoy
                </h2>
                <p className="text-sm text-muted-foreground">{hoyConNombre()}</p>
            </div>
            <div className="rounded-2xl bg-card border border-border p-4">
                <CampoDePeso api={api} ultimo={pesado || ultimo} diasAtras={0} abrir compacto
                    conLosDosNumeros={false} onGuardado={setPesado} onSemana={setSemana} />
            </div>
            {listo && (
                <button type="button" onClick={onAbrir} data-testid="quincenal-abrir-tras-peso"
                    className="w-full rounded-xl py-3 text-sm font-bold text-white uppercase tracking-wider"
                    style={{ backgroundColor: ORANGE }}>
                    Abrir el reporte quincenal
                </button>
            )}
        </div>
    );
};

const QuincenalResumen = ({ api, valores, set, onConfirmar }) => {
    const [resumen, setResumen] = useState(null);
    const [cargando, setCargando] = useState(true);
    const [fallo, setFallo] = useState(false);

    const cargar = useCallback(async () => {
        setCargando(true);
        try {
            // Con SU día (`hoyLocal`): el mismo con el que abajo se mira si el peso es de hoy.
            const r = await api.get('/reports/quincenal/resumen', { params: { hoy: hoyLocal() } });
            setResumen(r.data);
            setFallo(false);
        } catch (e) {
            console.error('No se pudo cargar el resumen del quincenal', e);
            setFallo(true);
        } finally {
            setCargando(false);
        }
    }, [api]);

    useEffect(() => { cargar(); }, [cargar]);

    const actual = resumen?.peso?.actual;
    const deHoy = actual != null && resumen?.peso?.fecha === hoyLocal();
    useEffect(() => {
        if (deHoy) set('weight', String(actual));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deHoy, actual]);

    if (cargando && !resumen) {
        return (
            <div className="animate-pulse space-y-3" data-testid="quincenal-resumen">
                <div className="h-20 bg-card rounded-2xl" />
                <div className="h-44 bg-card rounded-2xl" />
            </div>
        );
    }
    if (fallo && !resumen) {
        return (
            <div className="rounded-2xl border border-border bg-card p-4" data-testid="quincenal-resumen">
                <p className="text-sm text-foreground">No hemos podido traer tus datos de estos días.</p>
                <button type="button" onClick={cargar} data-testid="quincenal-resumen-reintentar"
                    className="mt-2 text-sm font-bold" style={{ color: ORANGE }}>
                    Probar otra vez
                </button>
            </div>
        );
    }

    if (!deHoy) {
        const u = resumen?.peso?.ultimo;
        return <PuertaDelPeso api={api} ultimo={u ? { valor: u.valor, fecha: u.fecha } : null} onAbrir={cargar} />;
    }

    const peso = resumen?.peso || {};
    const cabecera = peso.cabecera;
    const sinDatos = resumen?.sin_datos;
    return (
        <div className="space-y-4" data-testid="quincenal-resumen">
            <CabeceraDelQuincenal antetitulo="Informe quincenal" titulo={resumen?.titulo || 'Informe quincenal'}
                sub={resumen?.subtitulo} paso={1} />

            {cabecera && (
                <div className="text-center py-2" data-testid="quincenal-cifra">
                    <p className={`text-4xl font-bold tabular-nums ${cabecera.numero < 0 ? 'text-emerald-500' : 'text-foreground'}`}
                        style={{ fontFamily: 'Barlow Condensed' }}>
                        {cabecera.valor}
                    </p>
                    <p className="text-xs text-muted-foreground">{cabecera.pie}</p>
                </div>
            )}

            <Seccion titulo="Tu peso" testid="quincenal-peso">
                <FilasDePeso filas={peso.filas} prefijo="quincenal" />
            </Seccion>

            {sinDatos ? (
                <div className="rounded-2xl border p-4 space-y-2" data-testid="quincenal-sin-datos"
                    style={{ borderColor: `${ORANGE}55`, backgroundColor: `${ORANGE}0D` }}>
                    <p className="text-sm font-bold text-foreground">{sinDatos.titulo}</p>
                    <p className="text-sm text-muted-foreground">{sinDatos.texto}</p>
                    <p className="text-sm text-muted-foreground">{sinDatos.pie}</p>
                </div>
            ) : (
                <>
                    <Seccion titulo="Lo que has hecho" testid="quincenal-hecho">
                        {(resumen?.hecho || []).map((f, i) => (
                            <div key={f.clave} data-testid={`quincenal-fila-${f.clave}`}
                                className={`py-2.5 space-y-1.5 ${i ? 'border-t border-border' : ''}`}>
                                <div className="flex items-center justify-between gap-3">
                                    <span className="text-sm text-foreground/80">{f.etiqueta}</span>
                                    <Valor fila={f} />
                                </div>
                                {!f.sin_dato && <BarraDeTramos hechos={f.hecho} de={f.de} />}
                            </div>
                        ))}
                    </Seccion>

                    <Seccion titulo={`Cómo te has sentido · ${resumen?.sensaciones?.pie || ''}`}
                        testid="quincenal-sensaciones">
                        <FilasDeSensaciones filas={resumen?.sensaciones?.filas} prefijo="quincenal" />
                    </Seccion>
                </>
            )}

            <button type="button" onClick={onConfirmar} data-testid="quincenal-resumen-continuar"
                className="w-full rounded-xl py-3 text-sm font-bold text-white uppercase tracking-wider"
                style={{ backgroundColor: ORANGE }}>
                Continuar
            </button>
        </div>
    );
};

export default QuincenalResumen;
